import { Controller } from "@hotwired/stimulus";
import { renderStreamMessage, visit } from "@hotwired/turbo";

type SharingPerson = { email: string; pending: boolean };

// UI lifecycle only. Account state and all permissions remain server-owned.
export default class AccountsController extends Controller<HTMLElement> {
  static targets = ["status", "sequence", "groupChoice", "dialog", "inlineDisplay", "inlineForm", "inlineInput", "toggleInput", "toggleValue", "sharingEmpty", "sharingPeople"];
  declare readonly hasSharingEmptyTarget: boolean;
  declare readonly sharingEmptyTarget: HTMLElement;
  declare readonly hasSharingPeopleTarget: boolean;
  declare readonly sharingPeopleTarget: HTMLUListElement;
  declare readonly hasDialogTarget: boolean;
  declare readonly dialogTarget: HTMLDialogElement;
  declare readonly inlineDisplayTarget: HTMLElement;
  declare readonly inlineFormTarget: HTMLFormElement;
  declare readonly inlineInputTarget: HTMLInputElement;
  declare readonly hasToggleInputTarget: boolean;
  declare readonly toggleInputTarget: HTMLInputElement;
  declare readonly toggleValueTarget: HTMLInputElement;
  private toggleBefore?: boolean;
  static values = { confirm: String, statusUrl: String, pending: Boolean, knownGroups: String, people: Object, returnUrl: String };
  declare readonly returnUrlValue: string;
  declare readonly peopleValue: Record<string, SharingPerson[]>;
  declare readonly confirmValue: string;
  declare readonly statusUrlValue: string;
  declare readonly pendingValue: boolean;
  declare readonly knownGroupsValue: string;
  private importKnown = "";
  private importTimer?: ReturnType<typeof setTimeout>;
  private importRequest?: AbortController;
  private importAttempts = 0;
  private cacheMeta?: HTMLMetaElement;
  private previousCacheControl?: string | null;
  declare readonly hasStatusTarget: boolean;
  declare readonly statusTarget: HTMLElement;
  declare readonly hasSequenceTarget: boolean;
  declare readonly sequenceTarget: HTMLOListElement;
  declare readonly groupChoiceTarget: HTMLSelectElement;

  private submitting = false;
  private origin?: HTMLElement;
  private originHref?: string;
  private originCard?: string;
  private originLabel?: string;
  private observer?: MutationObserver;
  private focusTask?: number;
  private disabledButtons: HTMLButtonElement[] = [];
  private inlineFocus?: { feedback: string; element: HTMLElement };

  connect() {
    if (this.element.hasAttribute("data-accounts-page")) {
      const existing = document.head.querySelector<HTMLMetaElement>('meta[name="turbo-cache-control"]');
      this.cacheMeta = existing || document.createElement("meta");
      this.previousCacheControl = existing?.getAttribute("content") ?? null;
      this.cacheMeta.name = "turbo-cache-control";
      this.cacheMeta.content = "no-cache";
      if (!existing) document.head.append(this.cacheMeta);
    }
    this.importKnown = this.knownGroupsValue;
    if (this.pendingValue && this.statusUrlValue) this.scheduleImportCheck();
    if (this.element.querySelector("[data-accounts-editor-heading]")) this.focusEditor();
    if (this.hasSequenceTarget) {
      this.refreshOrder();
      this.observer = new MutationObserver(() => this.refreshOrder());
      this.observer.observe(this.sequenceTarget, { childList: true });
    }
  }

  disconnect() {
    this.observer?.disconnect();
    if (this.importTimer !== undefined) clearTimeout(this.importTimer);
    this.importRequest?.abort();
    if (this.cacheMeta?.isConnected && this.cacheMeta.content === "no-cache") {
      if (this.previousCacheControl === null) this.cacheMeta.remove();
      else if (this.previousCacheControl !== undefined) this.cacheMeta.content = this.previousCacheControl;
    }
    if (this.focusTask !== undefined) cancelAnimationFrame(this.focusTask);
  }

  private scheduleImportCheck() {
    if (this.importAttempts >= 60) return;
    this.importTimer = setTimeout(() => this.checkImport(), 5000);
  }

  private async checkImport() {
    this.importAttempts++;
    this.importRequest = new AbortController();
    try {
      const url = new URL(this.statusUrlValue, window.location.origin);
      const visibleGroups = Array.from(this.element.querySelectorAll<HTMLElement>("#accounts-owned-list [data-accounts-group-id]"));
      this.importKnown = visibleGroups.map((card) => card.dataset.accountsGroupId).join(",");
      url.searchParams.set("known_groups", this.importKnown);
      const response = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
        signal: this.importRequest.signal,
      });
      if (!response.ok || !this.element.isConnected || !response.headers.get("Content-Type")?.includes("text/vnd.turbo-stream.html")) return;
      const html = await response.text();
      if (!this.element.isConnected) return;
      renderStreamMessage(html);
      this.importKnown = response.headers.get("X-Accounts-Known") || this.importKnown;
      if (response.headers.get("X-Accounts-Pending") === "true") this.scheduleImportCheck();
    } catch {
      // Navigation aborts silently; a failed status read never retries mutations.
    }
  }

  remember(event: MouseEvent) {
    const target = (event.target as Element).closest<HTMLElement>(
      'a[data-turbo-frame="accounts-editor"], form[data-turbo-frame="accounts-editor"] button[type="submit"]',
    );
    if (!target || target.closest("#accounts-editor") || target.closest("form[data-accounts-inline]")) return;
    if (target.closest("form") && this.element.querySelector("#accounts-editor")?.children.length) return;
    this.origin = target;
    this.originHref = target instanceof HTMLAnchorElement ? target.getAttribute("href") || undefined : undefined;
    this.originCard = target.closest("[data-accounts-group-id], article")?.id;
    this.originLabel = target.getAttribute("aria-label") || target.textContent?.trim();
  }

  frameLoaded(event: Event) {
    if ((event.target as HTMLElement).id === "accounts-editor") this.focusEditor();
  }

  private workspaceController(): AccountsController | null {
    const root = this.element.closest<HTMLElement>("[data-accounts-page]");
    return root ? this.application.getControllerForElementAndIdentifier(root, "accounts") as AccountsController | null : null;
  }

  private focusEditor() {
    const root = this.workspaceController();
    if (root && root !== this) { root.focusEditor(); return; }
    this.focusTask = requestAnimationFrame(() => {
      const frame = this.element.closest("#accounts-editor") || this.element.querySelector("#accounts-editor") || this.element;
      const focus = frame.querySelector<HTMLElement>("[data-accounts-error]")
        || frame.querySelector<HTMLElement>("[data-accounts-editor-heading]");
      if (!focus) return;
      if (this.hasDialogTarget) {
        if (!this.dialogTarget.open) this.dialogTarget.showModal();
        this.dialogTarget.scrollTop = 0;
      }
      focus.focus({ preventScroll: true });
      if (!this.hasDialogTarget) focus.scrollIntoView({ block: "nearest" });
    });
  }

  private closeEditorDialog() {
    if (this.hasDialogTarget && this.dialogTarget.open) this.dialogTarget.close();
    this.restoreFocus();
  }

  private dismissEditor() {
    const frame = this.element.querySelector("#accounts-editor");
    const editor = frame?.querySelector<HTMLElement>('[data-controller~="accounts"]');
    const controller = editor ? this.application.getControllerForElementAndIdentifier(editor,"accounts") as AccountsController | null : null;
    if (controller?.submitting || frame?.querySelector('form[aria-busy="true"]')) return;
    frame?.replaceChildren();
    this.closeEditorDialog();
  }

  dialogCancel(event: Event) {
    event.preventDefault();
    this.dismissEditor();
  }

  dialogClosed() {
    if (this.dialogTarget.open) return;
    this.element.querySelector("#accounts-editor")?.replaceChildren();
    this.restoreFocus();
  }

  editInline() {
    this.inlineDisplayTarget.hidden = true;
    this.inlineFormTarget.hidden = false;
    this.inlineInputTarget.focus({ preventScroll: true });
    this.inlineInputTarget.select();
  }

  cancelInline(event: Event) {
    event.preventDefault();
    if (this.submitting) return;
    this.inlineFormTarget.reset();
    this.inlineFormTarget.hidden = true;
    this.inlineDisplayTarget.hidden = false;
    if (this.hasStatusTarget) this.statusTarget.textContent = "";
    this.inlineDisplayTarget.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
  }

  toggle(event: Event) {
    if (this.submitting) return;
    const checkbox = event.target as HTMLInputElement;
    this.toggleBefore = !checkbox.checked;
    this.toggleValueTarget.value = String(checkbox.checked);
    checkbox.form?.requestSubmit();
  }

  private restoreFocus() {
    let target = this.origin?.isConnected ? this.origin : undefined;
    if (!target && this.originHref) {
      target = Array.from(this.element.querySelectorAll<HTMLAnchorElement>("a[href]"))
        .find((link) => link.getAttribute("href") === this.originHref);
    }
    if (!target && this.originCard && this.originLabel) {
      target = Array.from(document.getElementById(this.originCard)?.querySelectorAll<HTMLElement>("button, a") || [])
        .find((button) => (button.getAttribute("aria-label") || button.textContent?.trim()) === this.originLabel);
    }
    (target || this.element.querySelector<HTMLElement>("[data-accounts-focus-fallback]"))?.focus({ preventScroll: true });
  }

  stream(event: CustomEvent) {
    const stream = event.target as HTMLElement;
    const target = stream.getAttribute("target");
    const conditionalEditorRemoval = stream.getAttribute("targets")?.startsWith("#accounts-editor ");
    if (this.returnUrlValue && target === this.element.dataset.accountsDetailId && stream.getAttribute("action") === "remove") {
      const render = event.detail.render;
      event.detail.render = async (element: HTMLElement) => {
        await render(element);
        visit(this.returnUrlValue, { frame: "main" });
      };
      return;
    }
    if (target?.startsWith("accounts-inline-")) {
      const pending = this.inlineFocus?.feedback === target ? this.inlineFocus : undefined;
      const render = event.detail.render;
      event.detail.render = async (element: HTMLElement) => {
        await render(element);
        this.focusTask = requestAnimationFrame(() => {
          if (pending && !pending.element.isConnected && document.activeElement === document.body && !(this.hasDialogTarget && this.dialogTarget.open)) {
            const control = document.getElementById(target)?.closest('[data-controller~="accounts"]');
            control?.querySelector<HTMLElement>('input[type="checkbox"]:not(:disabled), [data-accounts-target="inlineDisplay"] button, a')?.focus({ preventScroll: true });
          }
          if (pending === this.inlineFocus) this.inlineFocus = undefined;
        });
      };
      return;
    }
    if (target !== "accounts-editor" && target !== "accounts-message" && !conditionalEditorRemoval) return;
    const render = event.detail.render;
    event.detail.render = async (element: HTMLElement) => {
      await render(element);
      this.focusTask = requestAnimationFrame(() => {
        const frame = this.element.querySelector("#accounts-editor");
        if (conditionalEditorRemoval) {
          if (!frame?.children.length) this.closeEditorDialog();
        } else if (target === "accounts-editor") {
          if (frame?.children.length) this.focusEditor();
          else this.closeEditorDialog();
        } else if (!frame?.children.length) {
          this.element.querySelector<HTMLElement>('#accounts-message [role="alert"]')?.setAttribute("tabindex", "-1");
          this.element.querySelector<HTMLElement>('#accounts-message [role="alert"]')?.focus({ preventScroll: true });
        }
      });
    };
  }

  cancel(event: Event) {
    if (this.submitting) { event.preventDefault(); return; }
    const frame = this.element.closest("#accounts-editor");
    if (!frame) return;
    // Direct editor GETs retain a real Cancel destination as the HTML fallback.
    const root = frame.closest<HTMLElement>("[data-accounts-page]");
    if (!root) return;
    // Only a workspace dialog can be dismissed in place. On a direct editor
    // page, let the real link navigate back through the main Turbo frame.
    const controller = this.application.getControllerForElementAndIdentifier(root, "accounts") as AccountsController | null;
    if (!controller?.hasDialogTarget) return;
    event.preventDefault();
    // Root owns the saved origin and survives editor replacement.
    controller.dismissEditor();
  }

  submit(event: SubmitEvent) {
    const form = event.target as HTMLFormElement;
    const confirmation = event.submitter?.hasAttribute("data-accounts-pause") ? "" : form.dataset.accountsConfirm || this.confirmValue;
    if (this.submitting || (confirmation && !window.confirm(confirmation))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    this.submitting = true;
    if (form.hasAttribute("data-accounts-inline") && this.hasStatusTarget) {
      const root = this.workspaceController();
      if (root) root.inlineFocus = { feedback: this.statusTarget.id, element: this.element };
    }
  }

  busy(event: Event) {
    const form = event.target as HTMLFormElement;
    this.submitting = true;
    form.setAttribute("aria-busy", "true");
    // Only submission controls are disabled: keep all submitted values intact.
    this.disabledButtons = Array.from(form.querySelectorAll<HTMLButtonElement>('button[type="submit"]')).filter((b) => !b.disabled);
    this.disabledButtons.forEach((button) => { button.disabled = true; });
    if (this.hasToggleInputTarget) this.toggleInputTarget.disabled = true;
    if (this.hasStatusTarget) this.statusTarget.textContent = "Saving…";
  }

  finished(event: CustomEvent) {
    const form = event.target as HTMLFormElement;
    this.submitting = false;
    form.removeAttribute("aria-busy");
    this.disabledButtons.forEach((button) => { button.disabled = false; });
    this.disabledButtons = [];
    if (this.hasToggleInputTarget) {
      this.toggleInputTarget.disabled = false;
      if (!event.detail.success && this.toggleBefore !== undefined) {
        this.toggleInputTarget.checked = this.toggleBefore;
        this.toggleValueTarget.value = String(this.toggleBefore);
      }
      this.toggleBefore = undefined;
    }
    if (form.hasAttribute("data-accounts-inline")) {
      if (!event.detail.success && this.hasStatusTarget && this.statusTarget.textContent === "Saving…") {
        this.statusTarget.textContent = this.hasToggleInputTarget
          ? "Couldn’t update this setting. It hasn’t changed. Try again."
          : "Couldn’t save. Your changes are still here. Try again.";
      }
      return;
    }
    if (this.hasStatusTarget && this.statusTarget.textContent === "Saving…") {
      this.statusTarget.textContent = event.detail.success
        ? "Changes saved."
        : "Couldn’t save. Your changes are still here. Check the error and try again.";
    }
  }

  missing(event: Event) {
    if ((event.target as HTMLElement).id !== "accounts-editor") return;
    event.preventDefault();
    const frame = event.target as HTMLElement;
    let error = frame.querySelector<HTMLElement>("[data-accounts-error]");
    if (!error) {
      error = document.createElement("p");
      error.dataset.accountsError = "";
      error.className = "budgeter-alert budgeter-alert--error";
      error.setAttribute("role", "alert");
      error.tabIndex = -1;
      frame.prepend(error);
    }
    error.textContent = "Couldn’t open these settings. Please try again.";
    this.focusEditor();
  }

  private rows(): HTMLElement[] { return Array.from(this.sequenceTarget.children) as HTMLElement[]; }

  private refreshOrder() {
    const rows = this.rows();
    rows.forEach((row, index) => {
      const earlier = row.querySelector<HTMLButtonElement>('[data-action="accounts#moveUp"]');
      const later = row.querySelector<HTMLButtonElement>('[data-action="accounts#moveDown"]');
      if (earlier) earlier.disabled = index === 0;
      if (later) later.disabled = index === rows.length - 1;
      const input = row.querySelector<HTMLInputElement>('input[name="cutover"]');
      if (input) input.disabled = index === rows.length - 1;
      const label = row.querySelector<HTMLElement>("[data-accounts-cutover-label]");
      if (label) label.hidden = index === rows.length - 1;
    });
    const currency = rows[0]?.dataset.accountsCurrency;
    const selected = new Set(rows.map((row) => row.dataset.accountsMember));
    Array.from(this.groupChoiceTarget.options).forEach((option) => {
      if (!option.value) return;
      const template = Array.from(this.element.querySelectorAll<HTMLTemplateElement>("template[data-accounts-group]"))
        .find((t) => t.dataset.accountsGroup === option.value);
      const overlaps = Array.from(template?.content.querySelectorAll<HTMLElement>("[data-accounts-member]") || [])
        .some((row) => selected.has(row.dataset.accountsMember));
      option.disabled = overlaps || !!(currency && option.dataset.accountsCurrency !== currency);
    });
    this.refreshSharing(rows);
    this.refreshRanges(rows);
  }

  previewDates() { this.refreshRanges(this.rows()); }

  private refreshRanges(rows: HTMLElement[]) {
    const start = this.element.querySelector<HTMLInputElement>('input[name="start"]')?.value || "";
    const end = this.element.querySelector<HTMLInputElement>('input[name="end"]')?.value || "";
    const cutovers = rows.map((row) => row.querySelector<HTMLInputElement>('input[name="cutover"]')?.value || "");
    rows.forEach((row, index) => {
      const preview = row.querySelector<HTMLElement>("[data-accounts-range-preview]");
      if (!preview) return;
      const from = index === rows.length - 1 ? start : cutovers[index];
      const nextStart = index > 0 ? cutovers[index - 1] : "";
      let until = end;
      if (index > 0 && nextStart) {
        const boundary = new Date(`${nextStart}T00:00:00Z`);
        boundary.setUTCDate(boundary.getUTCDate() - 1);
        until = Number.isNaN(boundary.getTime()) ? "" : boundary.toISOString().slice(0, 10);
      }
      if ((index < rows.length - 1 && !from) || (index > 0 && !nextStart)) {
        preview.textContent = "Choose the first day to use each account. Then check the dates here.";
      } else if (from && until && from > until) {
        preview.textContent = "Check the dates: the end date is before the start date.";
      } else {
        preview.textContent = from && until ? `Use transactions from ${from} to ${until}, including both dates.`
          : from ? `Use transactions from ${from} onward.`
          : until ? `Use all transactions up to and including ${until}.`
          : "Use all transactions, with no date limits.";
      }
    });
  }

  private refreshSharing(rows: HTMLElement[]) {
    if (!this.hasSharingPeopleTarget || !this.hasSharingEmptyTarget) return;
    const people = new Map<string, SharingPerson>();
    for (const row of rows) {
      for (const person of this.peopleValue[row.dataset.accountsGroup || ""] || []) {
        const email = person.email.trim().toLowerCase();
        if (!email) continue;
        const previous = people.get(email);
        if (!previous || !person.pending || previous.pending) {
          people.set(email, { email, pending: person.pending });
        }
      }
    }
    const recipients = Array.from(people.values()).sort((a, b) => a.email < b.email ? -1 : a.email > b.email ? 1 : 0);
    const items = recipients.map((person) => {
      const item = document.createElement("li");
      item.textContent = person.email + (person.pending ? " (Invited)" : "");
      return item;
    });
    this.sharingPeopleTarget.replaceChildren(...items);
    this.sharingEmptyTarget.hidden = recipients.length > 0;
  }

  private move(event: Event, direction: number) {
    const button = event.currentTarget as HTMLButtonElement;
    const row = button.closest<HTMLElement>("[data-accounts-member]");
    if (!row) return;
    const rows = this.rows();
    const index = rows.indexOf(row);
    const sibling = rows[index + direction];
    if (!sibling) return;
    // Handovers belong to sequence positions, not physical feed identities.
    const dates = rows.map((r) => r.querySelector<HTMLInputElement>('input[name="cutover"]')!.value);
    if (direction < 0) sibling.before(row); else sibling.after(row);
    this.rows().forEach((r, i) => { r.querySelector<HTMLInputElement>('input[name="cutover"]')!.value = dates[i]; });
    this.refreshOrder();
    // The operated boundary button may now be disabled; focus the other one.
    const focus = button.disabled ? row.querySelector<HTMLButtonElement>(direction < 0 ? '[data-action="accounts#moveDown"]' : '[data-action="accounts#moveUp"]') : button;
    focus?.focus({ preventScroll: true });
    if (this.hasStatusTarget) this.statusTarget.textContent = `Account moved to position ${index + direction + 1}.`;
  }

  moveUp(event: Event) { this.move(event, -1); }
  moveDown(event: Event) { this.move(event, 1); }

  removeMember(event: Event) {
    const row = (event.currentTarget as Element).closest<HTMLElement>("[data-accounts-member]");
    const next = row?.nextElementSibling || row?.previousElementSibling;
    row?.remove();
    this.refreshOrder();
    (next?.querySelector("button:not(:disabled)") as HTMLElement | null || this.groupChoiceTarget)?.focus();
    if (this.hasStatusTarget) this.statusTarget.textContent = "Account removed from selection. Save to keep your changes.";
  }

  addGroup() {
    const key = this.groupChoiceTarget.value;
    if (this.groupChoiceTarget.selectedOptions[0]?.disabled) return;
    const template = Array.from(this.element.querySelectorAll<HTMLTemplateElement>("template[data-accounts-group]"))
      .find((t) => t.dataset.accountsGroup === key);
    if (!template) return;
    const additions = Array.from(template.content.querySelectorAll<HTMLElement>("[data-accounts-member]"));
    const existing = new Set(this.rows().map((r) => r.dataset.accountsMember));
    if (additions.some((r) => existing.has(r.dataset.accountsMember))) {
      if (this.hasStatusTarget) this.statusTarget.textContent = "One of these accounts is already selected. Remove it first.";
      return;
    }
    this.sequenceTarget.append(template.content.cloneNode(true));
    this.refreshOrder();
    this.groupChoiceTarget.value = "";
    if (this.hasStatusTarget) this.statusTarget.textContent = "Accounts added. Check their order and dates before saving.";
  }
}
